import { BadgeVenta } from '@/components/BadgeVenta';
import { LogoAdelante } from '@/components/layout/LogoAdelante';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useHoras, useNomina, useSubcontratos } from '@/hooks/useManoObra';
import { useMatriz } from '@/hooks/useMatriz';
import { useReporteSemanal, useSemanas } from '@/hooks/useReportes';
import { useResumenMes } from '@/hooks/useResumenMes';
import { calcularManoObra } from '@/lib/calcularManoObra';
import { exportarReporteXlsx } from '@/lib/exportarReporte';
import { FILTRO_VENTA_LABEL, type FiltroVenta } from '@/lib/filtroVenta';
import { cn } from '@/lib/utils';
import { formatearCRC } from '@adelante/shared';
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  CircleNotch,
  ClockCounterClockwise,
  CurrencyDollar,
  GridFour,
  Hammer,
  MicrosoftExcelLogo,
  Printer,
  Ruler,
  Table as TableIcon,
  Warning,
} from '@phosphor-icons/react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { HistoricoVista } from './HistoricoVista';
import { M2Vista } from './M2Vista';
import { ManoObraReporteVista } from './ManoObraReporteVista';
import { ObraAvanceDialog } from './ObraAvanceDialog';
import { PendientesVista } from './PendientesVista';
import { ResumenMesFinancieroVista } from './ResumenMesFinancieroVista';
import { ResumenMesVista } from './ResumenMesVista';
import { MatrizAvanceVista } from './campo/MatrizAvanceVista';

/**
 * Reportes semanales (Fase 5, BLOCKER de lanzamiento).
 *
 * Tres sub-tabs (replicando la app original):
 *   - Producción: KPIs + tabla por obra (snapshots del cierre A).
 *   - Mano de Obra: próximamente — depende de capturar nómina/horas.
 *   - Matriz: próximamente — diseño compartido con Reportes para ir luego.
 *
 * Imprime via window.print() + @media print (sin jsPDF, igual que el original).
 */

type Tab = 'produccion' | 'financiero' | 'mo' | 'matriz' | 'historico' | 'm2' | 'pendientes';

const TAB_LABEL: Record<Tab, string> = {
  produccion: 'Producción',
  financiero: 'Financiero',
  mo: 'Mano de Obra',
  matriz: 'Matriz',
  historico: 'Histórico',
  m2: 'Avance m²',
  pendientes: 'Pendientes',
};

export function ReportesPantalla() {
  const semanasQ = useSemanas();
  const semanas = semanasQ.data?.data ?? [];

  const [semanaSel, setSemanaSel] = useState<number | null>(null);
  // Auto-selecciona la semana ACTUAL (abierta) al cargar; si no hay abierta, la
  // más reciente. (semanas viene ordenada DESC por fecha.)
  useEffect(() => {
    if (semanaSel != null || semanas.length === 0) return;
    const abierta = semanas.find((s) => s.estado === 'abierta');
    setSemanaSel(Number(abierta?.id ?? semanas[0]?.id));
  }, [semanas, semanaSel]);

  // Filtro de estado de venta (afecta Producción + Mano de Obra; lo aplica el
  // backend al reporte → un solo origen de datos consistente).
  const [filtroVenta, setFiltroVenta] = useState<FiltroVenta>('todas');
  const reporteQ = useReporteSemanal(semanaSel, filtroVenta);
  const reporte = reporteQ.data?.data ?? null;

  const [tab, setTab] = useState<Tab>('produccion');

  // Datos de M.O. (cacheados, compartidos con los tabs). Alimentan las columnas
  // M.O./Materiales de la tabla de Producción y el export Excel.
  const { data: nominaD } = useNomina();
  const { data: horasD } = useHoras();
  const { data: subcD } = useSubcontratos();
  const resumenMes = useResumenMes(semanas, semanaSel, filtroVenta);
  const matrizQ = useMatriz(null, undefined, semanaSel);

  const moCalc = useMemo(
    () =>
      reporte && semanaSel != null
        ? calcularManoObra(
            semanaSel,
            reporte,
            nominaD?.data ?? [],
            horasD?.data ?? [],
            subcD?.data ?? [],
          )
        : null,
    [reporte, semanaSel, nominaD, horasD, subcD],
  );
  // obra_codigo → M.O. de la semana (nómina prorrateada por horas + subcontratos).
  const moPorObra = useMemo(
    () => new Map((moCalc?.obras ?? []).map((o) => [o.codigo, o.moTotal])),
    [moCalc],
  );

  /** Export a Excel COMPLETO: todas las hojas (Resumen, Mes, Detalle, M.O., Matriz). */
  async function exportarExcel() {
    if (!reporte) return;
    try {
      await exportarReporteXlsx(reporte, {
        resumenMes,
        moCalc,
        matriz: matrizQ.data?.data ?? null,
      });
    } catch (e) {
      toast.error('No se pudo exportar el Excel', {
        description: e instanceof Error ? e.message : 'Error',
      });
    }
  }

  const semanaActual = semanas.find((s) => Number(s.id) === semanaSel) ?? null;

  // Navegación de semana con flechas (semanas viene DESC: 0 = más reciente).
  const idxSem = semanas.findIndex((s) => Number(s.id) === semanaSel);
  const irSem = (delta: number) => {
    const s = semanas[idxSem + delta];
    if (s) setSemanaSel(Number(s.id));
  };

  return (
    <div className="space-y-4">
      {/* Encabezado SOLO para impresión / PDF (oculto en pantalla). */}
      <div className="solo-impresion mb-4 border-b border-zinc-300 pb-3">
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-2">
            <LogoAdelante altura={20} />
            <span className="text-lg font-bold tracking-wider">ADELANTE.</span>
          </div>
          <div className="text-right text-[11px] text-zinc-600">
            {semanaActual && (
              <p className="text-sm font-semibold text-foreground">
                Reporte {TAB_LABEL[tab]} — Semana {semanaActual.numero_semana}/{semanaActual.anio}
              </p>
            )}
            {semanaActual && (
              <p>
                {semanaActual.fecha_inicio.slice(0, 10)} a {semanaActual.fecha_fin.slice(0, 10)} ·{' '}
                {FILTRO_VENTA_LABEL[filtroVenta]}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Encabezado + acciones (no se imprimen) */}
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Reportes semanales</h1>
          <p className="text-xs text-muted-foreground">
            Lectura directa de los snapshots inmutables del cierre.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Semana anterior"
            disabled={idxSem < 0 || idxSem >= semanas.length - 1}
            onClick={() => irSem(1)}
          >
            <CaretLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
          </Button>
          <Select
            value={semanaSel ? String(semanaSel) : ''}
            onValueChange={(v) => setSemanaSel(Number(v))}
          >
            <SelectTrigger className="w-[360px]">
              <SelectValue placeholder="Seleccionar semana…" />
            </SelectTrigger>
            <SelectContent>
              {semanas.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.anio} · Semana {s.numero_semana} ({s.estado}) ·{' '}
                  {rangoCorto(s.fecha_inicio, s.fecha_fin)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Semana siguiente"
            disabled={idxSem <= 0}
            onClick={() => irSem(-1)}
          >
            <CaretRight className="h-4 w-4" weight="bold" aria-hidden="true" />
          </Button>
        </div>
        {(tab === 'produccion' || tab === 'financiero' || tab === 'mo') && (
          <Select value={filtroVenta} onValueChange={(v) => setFiltroVenta(v as FiltroVenta)}>
            <SelectTrigger className="w-[180px]" title="Filtrar por estado de venta">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las ventas</SelectItem>
              <SelectItem value="formalizadas">Formalizadas</SelectItem>
              <SelectItem value="no_formalizadas">No formalizadas</SelectItem>
            </SelectContent>
          </Select>
        )}
        {(tab === 'produccion' || tab === 'financiero' || tab === 'mo') && (
          <Button
            variant="outline"
            onClick={() => void exportarExcel()}
            disabled={!reporte}
            title="Exportar a Excel"
          >
            <MicrosoftExcelLogo className="h-4 w-4" weight="bold" aria-hidden="true" />
            Excel
          </Button>
        )}
        <Button variant="outline" onClick={() => window.print()} disabled={!reporte}>
          <Printer className="h-4 w-4" weight="bold" aria-hidden="true" />
          Imprimir / PDF
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border print:hidden">
        <TabBtn
          icon={TableIcon}
          label="Producción"
          activo={tab === 'produccion'}
          onClick={() => setTab('produccion')}
        />
        <TabBtn
          icon={CurrencyDollar}
          label="Financiero"
          activo={tab === 'financiero'}
          onClick={() => setTab('financiero')}
        />
        <TabBtn
          icon={Hammer}
          label="Mano de Obra"
          activo={tab === 'mo'}
          onClick={() => setTab('mo')}
        />
        <TabBtn
          icon={GridFour}
          label="Matriz"
          activo={tab === 'matriz'}
          onClick={() => setTab('matriz')}
        />
        <TabBtn
          icon={ClockCounterClockwise}
          label="Histórico"
          activo={tab === 'historico'}
          onClick={() => setTab('historico')}
        />
        <TabBtn icon={Ruler} label="Avance m²" activo={tab === 'm2'} onClick={() => setTab('m2')} />
        <TabBtn
          icon={Warning}
          label="Pendientes"
          activo={tab === 'pendientes'}
          onClick={() => setTab('pendientes')}
        />
      </div>

      {/* Contenido */}
      {reporteQ.isLoading && (
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando reporte…
        </div>
      )}

      {reporte && tab === 'produccion' && (
        <TabProduccion
          reporte={reporte}
          semanas={semanas}
          semanaSel={semanaSel}
          filtroVenta={filtroVenta}
          moPorObra={moPorObra}
        />
      )}
      {reporte && tab === 'financiero' && (
        <TabFinanciero
          reporte={reporte}
          semanas={semanas}
          semanaSel={semanaSel}
          filtroVenta={filtroVenta}
        />
      )}
      {tab === 'mo' && (
        <ManoObraReporteVista
          semana={semanaSel}
          reporte={reporte}
          semanas={semanas}
          filtroVenta={filtroVenta}
        />
      )}
      {tab === 'matriz' && <MatrizAvanceVista proyecto={null} semana={semanaSel} readonly />}
      {tab === 'historico' && <HistoricoVista semana={semanaSel} />}
      {tab === 'm2' && <M2Vista semana={semanaSel} />}
      {tab === 'pendientes' && <PendientesVista />}
    </div>
  );
}

/** '15/10 → 21/10' a partir de fechas ISO. */
function rangoCorto(ini: string, fin: string): string {
  const f = (iso: string) => iso.slice(0, 10).split('-').reverse().slice(0, 2).join('/');
  return `${f(ini)} → ${f(fin)}`;
}

function TabBtn({
  icon: Icon,
  label,
  activo,
  onClick,
}: {
  icon: typeof TableIcon;
  label: string;
  activo: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
        activo
          ? 'border-adelante-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" weight="bold" aria-hidden="true" />
      {label}
    </button>
  );
}

// ===========================================================================
// Tab: Producción
// ===========================================================================

function TabProduccion({
  reporte,
  semanas,
  semanaSel,
  filtroVenta,
  moPorObra,
}: {
  reporte: import('@adelante/shared').ReporteSemanal;
  semanas: import('@adelante/shared').SemanaOperativa[];
  semanaSel: number | null;
  filtroVenta: FiltroVenta;
  moPorObra: Map<string, number>;
}) {
  const { semana, cierre, totales, obras, base_semanal: baseSemanal } = reporte;
  const [busqueda, setBusqueda] = useState('');
  const porProyecto = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const map = new Map<string, typeof obras>();
    for (const o of obras) {
      if (q && !o.codigo.toLowerCase().includes(q)) continue;
      const arr = map.get(o.proyecto_codigo) ?? [];
      arr.push(o);
      map.set(o.proyecto_codigo, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [obras, busqueda]);

  const cronoOk = totales.crono_real_prom >= totales.crono_esperado_prom;
  const costoOk = totales.costo_real_prom >= totales.costo_esperado_prom;

  // Tabla en 2 vistas para no amontonar columnas: "Semana" (logrado/esperado de
  // la semana) y "Acumulado" (estado global de la obra).
  const [vistaTabla, setVistaTabla] = useState<'semana' | 'acumulado'>('semana');
  // Obra seleccionada para el drill-down (sub-partidas que avanzaron la semana).
  const [obraDetalle, setObraDetalle] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {/* Encabezado del reporte (se imprime) */}
      <Card className="space-y-2 p-4 print:border-0 print:p-0 print:shadow-none">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-xl font-semibold">
              Producción — Semana {semana.numero_semana} · {semana.anio}
            </h2>
            <p className="text-xs text-muted-foreground">
              {semana.fecha_inicio.slice(0, 10)} → {semana.fecha_fin.slice(0, 10)} ·{' '}
              {semana.dias_efectivos} días efectivos · {semana.estado}
            </p>
          </div>
          {cierre ? (
            <Badge className="bg-adelante-primary text-adelante-foreground">
              Cierre · {cierre.fecha_cierre.slice(0, 10)}
            </Badge>
          ) : semana.estado === 'cerrada' ? (
            <Badge variant="outline" className="border-amber-300 text-amber-700">
              Cerrada · sin Cierre (estado vivo)
            </Badge>
          ) : (
            <Badge variant="outline">Semana abierta (estado vivo)</Badge>
          )}
        </div>
      </Card>

      {/* Aviso: sin cierre de la semana anterior no hay "logrado de la semana". */}
      {!baseSemanal && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span>
            ⓘ El <strong>avance de la semana</strong> (% Crono/Costo Real, m² y ₡ semanales) se
            calcula restando contra el cierre de la <strong>semana anterior</strong>. Todavía no
            existe ese cierre, así que esas cifras aparecen como “—”. Se activan al correr el{' '}
            <strong>Cierre</strong> semanal. Lo acumulado, presupuesto y faltante sí son reales.
          </span>
        </div>
      )}

      {/* Cronograma + Costo (semana) */}
      <Seccion
        titulo={
          totales.parcial
            ? 'Cronograma y costo — semana · Parcial (semana abierta)'
            : 'Cronograma y costo — semana'
        }
      >
        <Kpi
          label="% Crono Real"
          value={baseSemanal ? `${totales.crono_real_prom}%` : '—'}
          accent={baseSemanal ? (cronoOk ? 'lime' : 'red') : undefined}
        />
        <Kpi
          label="% Crono Esperado"
          value={`${totales.crono_esperado_prom}%`}
          detalle={`Meta del tipo × ${totales.eficiencia_crono}% eficiencia`}
        />
        <Kpi
          label="% Costo Real"
          value={baseSemanal ? `${totales.costo_real_prom}%` : '—'}
          accent={baseSemanal ? (costoOk ? 'lime' : 'red') : undefined}
          detalle={baseSemanal ? `Ponderado ₡: ${totales.costo_real_pond}%` : undefined}
        />
        <Kpi
          label="% Costo Esperado"
          value={`${totales.costo_esperado_prom}%`}
          detalle={`Ponderado ₡: ${totales.costo_esperado_pond}%`}
        />
        <Kpi
          label="Cumplimiento Crono"
          value={baseSemanal ? fmtCumpl(totales.cumplimiento_crono) : '—'}
          detalle="Real ÷ Esperado"
        />
        <Kpi
          label="Cumplimiento Costo"
          value={baseSemanal ? fmtCumpl(totales.cumplimiento_costo) : '—'}
          detalle={
            baseSemanal ? `Ponderado ₡: ${fmtCumpl(totales.cumplimiento_costo_pond)}` : undefined
          }
        />
        <Kpi
          label="Con Trabajo"
          value={baseSemanal ? totales.con_trabajo : '—'}
          accent="lime"
          detalle="Base de los promedios"
        />
        <Kpi
          label="Sin Trabajo"
          value={baseSemanal ? totales.sin_trabajo : '—'}
          accent={baseSemanal && totales.sin_trabajo > 0 ? 'red' : undefined}
          detalle="En ejecución sin avance · fuera del promedio"
        />
      </Seccion>
      <p className="-mt-2 text-[11px] text-muted-foreground">
        {totales.parcial && (
          <>
            <strong>Semana abierta — cifras parciales:</strong> el esperado es el de todas las casas
            en ejecución y el cumplimiento muestra cuánto de la meta de la semana se lleva logrado
            hasta hoy. Al cerrar la semana se calcula sobre las casas con trabajo.{' '}
          </>
        )}
        Promedios sobre las casas con trabajo en la semana. % Crono Esperado = un sprint por semana
        según el tipo de casa (100 / sprints) × eficiencia × días efectivos / 5. “Ponderado ₡” = Σ
        avance ₡ ÷ Σ presupuesto (las casas más caras pesan más). Para comparar como el Excel
        anterior: <strong>% Crono Esperado vs % Costo Real</strong>.
      </p>

      {/* Áreas */}
      <Seccion titulo="Áreas (m²)">
        <Kpi label="Área Total Construcción" value={fmtNum(totales.area_total)} />
        <Kpi label="Área Construida" value={fmtNum(totales.area_construida)} accent="lime" />
        <Kpi label="Área Por Construir" value={fmtNum(totales.area_por_construir)} />
        <Kpi
          label="m² Construidos Semana"
          value={baseSemanal ? fmtNum(totales.m2_semana) : '—'}
          accent="lime"
        />
        <Kpi label="m² Esperados Semana" value={fmtNum(totales.m2_esperado)} />
      </Seccion>

      {/* Económico (₡) */}
      <Seccion titulo="Económico (₡)">
        <Kpi
          label="Avance Económico Semana"
          value={baseSemanal ? formatearCRC(totales.monto_semana) : '—'}
          accent="lime"
          small
        />
        <Kpi
          label="Avance Económico Acumulado"
          value={formatearCRC(totales.monto_acumulado)}
          small
        />
        <Kpi label="Presupuesto Total" value={formatearCRC(totales.presupuesto_total)} small />
        <Kpi
          label="Faltante por Invertir"
          value={formatearCRC(totales.faltante)}
          accent="red"
          small
        />
      </Seccion>

      {/* Estado de obras — Construcción (en_ejecucion + en_espera) */}
      <Seccion titulo="Estado de obras — Construcción">
        <Kpi label="Construcción" value={totales.construccion} />
        <Kpi label="En Ejecución" value={totales.trabajando} />
        <Kpi label="En Espera" value={totales.en_espera_sprint} />
        <Kpi label="Congeladas" value={totales.en_espera} />
        <Kpi label="1N Activas" value={totales.tipo_1n} />
        <Kpi label="2N Activas" value={totales.tipo_2n} />
      </Seccion>

      {/* Fuera de construcción */}
      <Seccion titulo="Fuera de construcción">
        <Kpi label="Inactivas" value={totales.inactivas} />
        <Kpi label="Terminadas" value={totales.terminadas} />
        <Kpi label="Por Iniciar" value={totales.pendientes} />
      </Seccion>

      {/* Estado de obras — Venta (sobre todas las obras = tablero) */}
      <Seccion titulo="Estado de obras — Venta">
        <Kpi label="Formalizadas" value={totales.formalizadas} />
        <Kpi label="Entregadas" value={totales.entregadas} />
        <Kpi label="Reservadas" value={totales.reservadas} />
        <Kpi label="Disponibles" value={totales.disponibles} />
        <Kpi label="Sin Formalizar = Reservadas + Disponibles" value={totales.sin_formalizar} />
      </Seccion>

      {/* Resumen del mes (semanas del mes: esperado vs real acumulado).
       * En impresión arranca en página nueva. */}
      <div className="romper-pagina">
        <ResumenMesVista semanas={semanas} semanaSel={semanaSel} filtroVenta={filtroVenta} />
      </div>

      {/* Toggle de vista de la tabla */}
      <div className="flex items-center gap-2 print:hidden">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Detalle por obra:
        </span>
        <div className="inline-flex rounded-md border border-border p-0.5">
          <button
            type="button"
            onClick={() => setVistaTabla('semana')}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              vistaTabla === 'semana'
                ? 'bg-adelante-primary text-adelante-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Semana
          </button>
          <button
            type="button"
            onClick={() => setVistaTabla('acumulado')}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              vistaTabla === 'acumulado'
                ? 'bg-adelante-primary text-adelante-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Acumulado
          </button>
        </div>
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar obra…"
          className="h-8 w-48 text-xs"
        />
      </div>

      {/* Tabla por obra, agrupada por proyecto. En impresión arranca en página
       * nueva; las tablas largas FLUYEN entre páginas repitiendo el encabezado
       * de columnas (thead), sin recortar. */}
      <div className="romper-pagina space-y-4">
        {porProyecto.map(([proyecto, lista]) => (
          <Card key={proyecto} className="overflow-hidden print:shadow-none">
            <div className="border-b border-border bg-zinc-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Proyecto {proyecto} · {lista.length} obras ·{' '}
              {vistaTabla === 'semana' ? 'Vista Semana' : 'Vista Acumulado'}
            </div>
            {vistaTabla === 'semana' ? (
              <TablaSemana
                lista={lista}
                baseSemanal={baseSemanal}
                moPorObra={moPorObra}
                onObraClick={setObraDetalle}
              />
            ) : (
              <TablaAcumulado lista={lista} />
            )}
          </Card>
        ))}
      </div>

      <ObraAvanceDialog
        semana={semanaSel}
        obra={obraDetalle}
        onClose={() => setObraDetalle(null)}
      />
    </div>
  );
}

type ObraReporte = import('@adelante/shared').ReporteObra;

/** Obra "parqueada" esta semana (en espera, inactiva o en sprint de espera) →
 * se muestra opaca y NO suma en los totales. PERO si igual avanzó trabajo esa
 * semana (una cuadrilla que se desocupa y adelanta una casa en sprint de espera),
 * sus m²/₡ SÍ deben contar → solo es parqueada si NO avanzó. */
function obraParqueada(o: ObraReporte): boolean {
  const enReposo = o.estado === 'en_espera' || o.estado === 'inactiva' || o.sprint_es_espera;
  const avanzo = (o.crono_semana ?? 0) > 0.01 || (o.costo_semana ?? 0) > 0.01;
  return enReposo && !avanzo;
}

/** Totales por grupo: suma las columnas aditivas (m², ₡) y promedia las de %.
 * Solo cuenta obras ACTIVAS (excluye parqueadas: en espera, inactivas o en
 * sprint de espera) — esas no están trabajando, no deben inflar el total. */
function calcTotales(todas: ObraReporte[]) {
  const lista = todas.filter((o) => !obraParqueada(o));
  const n = lista.length || 1;
  const sum = (f: (o: ObraReporte) => number) => lista.reduce((a, o) => a + (f(o) || 0), 0);
  // % semanales: mismo criterio que el total del reporte (solo casas con trabajo).
  const prom = lista.filter((o) => o.en_promedio);
  const nProm = prom.length || 1;
  const sumProm = (f: (o: ObraReporte) => number) => prom.reduce((a, o) => a + (f(o) || 0), 0);
  // Esperado: en semana abierta incluye todas las en ejecución (en_esperado).
  const esp = lista.filter((o) => o.en_esperado);
  const nEsp = esp.length || 1;
  const sumEsp = (f: (o: ObraReporte) => number) => esp.reduce((a, o) => a + (f(o) || 0), 0);
  return {
    cronoEsp: sumEsp((o) => o.crono_esperado) / nEsp,
    cronoSem: sumProm((o) => o.crono_semana) / nProm,
    costoEsp: sumEsp((o) => o.costo_esperado) / nEsp,
    costoSem: sumProm((o) => o.costo_semana) / nProm,
    cronoAcum: sum((o) => o.crono_acumulado) / n,
    costoAcum: sum((o) => o.costo_acumulado) / n,
    m2Esp: sum((o) => o.m2_esperado),
    m2Sem: sum((o) => o.m2_semana),
    monto: sum((o) => o.monto_semana),
    m2: sum((o) => o.m2),
    m2Acum: sum((o) => o.m2_acumulado),
    subsComp: sum((o) => o.subs_completadas),
    subsTot: sum((o) => o.subs_total),
  };
}

/** Celda de nombre de obra: una sola línea, sin partir el código. */
function CeldaObra({ codigo }: { codigo: string }) {
  return (
    <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">{codigo}</TableCell>
  );
}

function CeldaTipo({ tipo }: { tipo: ObraReporte['tipo_casa'] }) {
  return (
    <TableCell>
      {tipo ? (
        <Badge variant="outline" className="font-mono text-[10px]">
          {tipo}
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )}
    </TableCell>
  );
}

// ---- Vista SEMANA: esperado vs logrado de la semana ----
type OrdenSemanaKey =
  | 'obra'
  | 'crono_esperado'
  | 'crono_semana'
  | 'costo_esperado'
  | 'costo_semana'
  | 'm2_esperado'
  | 'm2_semana'
  | 'monto_semana'
  | 'mo'
  | 'materiales'
  | 'subs'
  | 'venta';

/** Valor de una obra para ordenar por una columna (número o texto). */
function valorSemana(
  o: ObraReporte,
  key: OrdenSemanaKey,
  moPorObra: Map<string, number>,
): number | string {
  const mo = moPorObra.get(o.codigo) ?? 0;
  switch (key) {
    case 'obra':
      return o.codigo;
    case 'crono_esperado':
      return o.crono_esperado;
    case 'crono_semana':
      return o.crono_semana;
    case 'costo_esperado':
      return o.costo_esperado;
    case 'costo_semana':
      return o.costo_semana;
    case 'm2_esperado':
      return o.m2_esperado;
    case 'm2_semana':
      return o.m2_semana;
    case 'monto_semana':
      return o.monto_semana ?? 0;
    case 'mo':
      return mo;
    case 'materiales':
      return (o.monto_semana ?? 0) - mo;
    case 'subs':
      return o.subs_completadas;
    case 'venta':
      return o.estado_venta ?? '';
  }
}

function TablaSemana({
  lista,
  baseSemanal,
  moPorObra,
  onObraClick,
}: {
  lista: ObraReporte[];
  baseSemanal: boolean;
  moPorObra: Map<string, number>;
  onObraClick: (codigo: string) => void;
}) {
  const [orden, setOrden] = useState<{ key: OrdenSemanaKey; dir: 'asc' | 'desc' } | null>(null);

  const filas = useMemo(() => {
    if (!orden) return lista;
    const arr = [...lista];
    arr.sort((a, b) => {
      const va = valorSemana(a, orden.key, moPorObra);
      const vb = valorSemana(b, orden.key, moPorObra);
      const cmp =
        typeof va === 'string' || typeof vb === 'string'
          ? String(va).localeCompare(String(vb), 'es', { numeric: true })
          : va - vb;
      return orden.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [lista, orden, moPorObra]);

  function ordenarPor(key: OrdenSemanaKey) {
    setOrden((prev) => {
      if (prev?.key !== key)
        return { key, dir: key === 'obra' || key === 'venta' ? 'asc' : 'desc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }

  function Th({ k, label, alinear }: { k: OrdenSemanaKey; label: string; alinear?: 'right' }) {
    const activo = orden?.key === k;
    return (
      <TableHead className={alinear === 'right' ? 'text-right' : undefined}>
        <button
          type="button"
          onClick={() => ordenarPor(k)}
          className={cn(
            'inline-flex items-center gap-0.5 hover:text-foreground print:pointer-events-none',
            alinear === 'right' && 'flex-row-reverse',
            activo ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {label}
          {activo &&
            (orden?.dir === 'asc' ? (
              <CaretUp className="h-3 w-3" weight="bold" aria-hidden="true" />
            ) : (
              <CaretDown className="h-3 w-3" weight="bold" aria-hidden="true" />
            ))}
        </button>
      </TableHead>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <Th k="obra" label="Obra" />
          <Th k="crono_esperado" label="% Crono Esp." alinear="right" />
          <Th k="crono_semana" label="% Crono Sem." alinear="right" />
          <Th k="costo_esperado" label="% Costo Esp." alinear="right" />
          <Th k="costo_semana" label="% Costo Sem." alinear="right" />
          <Th k="m2_esperado" label="m² Sem. Esp." alinear="right" />
          <Th k="m2_semana" label="m² Sem." alinear="right" />
          <Th k="monto_semana" label="Av. Econ." alinear="right" />
          <Th k="mo" label="M.O." alinear="right" />
          <Th k="materiales" label="Materiales" alinear="right" />
          <Th k="subs" label="Subs sprint" alinear="right" />
          <Th k="venta" label="Venta" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {filas.map((o) => {
          const mo = moPorObra.get(o.codigo) ?? 0;
          const materiales = (o.monto_semana ?? 0) - mo;
          return (
            <TableRow
              key={o.codigo}
              className={cn('cursor-pointer hover:bg-muted/40', obraParqueada(o) && 'opacity-50')}
              onClick={() => onObraClick(o.codigo)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onObraClick(o.codigo);
                }
              }}
            >
              <CeldaObra codigo={o.codigo} />
              <TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
                {o.crono_esperado}%
              </TableCell>
              <TableCell
                className={cn(
                  'text-right font-mono tabular-nums text-sm font-semibold',
                  !baseSemanal
                    ? 'text-muted-foreground'
                    : o.crono_semana >= o.crono_esperado
                      ? 'text-adelante-primary-dark'
                      : 'text-red-600',
                )}
              >
                {baseSemanal ? `${o.crono_semana}%` : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
                {o.costo_esperado}%
              </TableCell>
              <TableCell
                className={cn(
                  'text-right font-mono tabular-nums text-sm font-semibold',
                  !baseSemanal
                    ? 'text-muted-foreground'
                    : o.costo_semana >= o.costo_esperado
                      ? 'text-adelante-primary-dark'
                      : 'text-red-600',
                )}
              >
                {baseSemanal ? `${o.costo_semana}%` : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
                {fmtNum(o.m2_esperado)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm">
                {baseSemanal ? fmtNum(o.m2_semana) : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm text-adelante-primary-dark">
                {baseSemanal ? fmtMonto(o.monto_semana) : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm">
                {baseSemanal && mo > 0 ? fmtMonto(mo) : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
                {baseSemanal && o.monto_semana > 0 ? fmtMonto(materiales) : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {o.subs_completadas}/{o.subs_total}
              </TableCell>
              <TableCell>
                <BadgeVenta estado={o.estado_venta} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      <TableFooter>
        {(() => {
          const t = calcTotales(lista);
          const activas = lista.filter((o) => !obraParqueada(o));
          const moTot = activas.reduce((a, o) => a + (moPorObra.get(o.codigo) ?? 0), 0);
          const matTot = activas.reduce(
            (a, o) => a + ((o.monto_semana ?? 0) - (moPorObra.get(o.codigo) ?? 0)),
            0,
          );
          return (
            <TableRow className="border-t-2">
              <TableCell className="whitespace-nowrap font-semibold">TOTAL</TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs text-muted-foreground">
                {fmtNum(t.cronoEsp)}%
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {baseSemanal ? `${fmtNum(t.cronoSem)}%` : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs text-muted-foreground">
                {fmtNum(t.costoEsp)}%
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {baseSemanal ? `${fmtNum(t.costoSem)}%` : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {fmtNum(t.m2Esp)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {baseSemanal ? fmtNum(t.m2Sem) : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold text-adelante-primary-dark">
                {baseSemanal ? fmtMonto(t.monto) : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {baseSemanal && moTot > 0 ? fmtMonto(moTot) : '—'}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold text-muted-foreground">
                {baseSemanal && t.monto > 0 ? fmtMonto(matTot) : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums text-xs font-semibold">
                {t.subsComp}/{t.subsTot}
              </TableCell>
              <TableCell />
            </TableRow>
          );
        })()}
      </TableFooter>
    </Table>
  );
}

// ---- Vista ACUMULADO: estado global de la obra ----
function TablaAcumulado({ lista }: { lista: ObraReporte[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-mono text-xs">Obra</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead className="text-right">Sprint</TableHead>
          <TableHead className="text-right">m²</TableHead>
          <TableHead className="text-right">m² Acum.</TableHead>
          <TableHead className="text-right">% Crono Acum.</TableHead>
          <TableHead className="text-right">% Costo Acum.</TableHead>
          <TableHead className="text-right">Subs sprint</TableHead>
          <TableHead>Venta</TableHead>
          <TableHead>Estado</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lista.map((o) => (
          <TableRow key={o.codigo} className={cn(obraParqueada(o) && 'opacity-50')}>
            <CeldaObra codigo={o.codigo} />
            <CeldaTipo tipo={o.tipo_casa} />
            <TableCell className="text-right tabular-nums">{o.sprint_actual ?? '—'}</TableCell>
            <TableCell className="text-right font-mono tabular-nums text-sm">
              {fmtNum(o.m2)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-sm">
              {fmtNum(o.m2_acumulado)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-sm font-semibold text-adelante-primary-dark">
              {o.crono_acumulado}%
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-sm font-semibold text-adelante-primary-dark">
              {o.costo_acumulado}%
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {o.subs_completadas}/{o.subs_total}
            </TableCell>
            <TableCell>
              <BadgeVenta estado={o.estado_venta} />
            </TableCell>
            <TableCell>
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px]',
                  o.sprint_es_espera &&
                    o.estado === 'en_ejecucion' &&
                    'border-violet-400 text-violet-700',
                )}
              >
                {o.sprint_es_espera && o.estado === 'en_ejecucion' ? 'espera' : o.estado}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        {(() => {
          const t = calcTotales(lista);
          return (
            <TableRow className="border-t-2">
              <TableCell className="whitespace-nowrap font-semibold">TOTAL</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {fmtNum(t.m2)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                {fmtNum(t.m2Acum)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs text-muted-foreground">
                {fmtNum(t.cronoAcum)}%
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs text-muted-foreground">
                {fmtNum(t.costoAcum)}%
              </TableCell>
              <TableCell className="text-right tabular-nums text-xs font-semibold">
                {t.subsComp}/{t.subsTot}
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          );
        })()}
      </TableFooter>
    </Table>
  );
}

// ===========================================================================
// Tab: Financiero (Indirecto · Venta · Utilidad)
// ===========================================================================

function TabFinanciero({
  reporte,
  semanas,
  semanaSel,
  filtroVenta,
}: {
  reporte: import('@adelante/shared').ReporteSemanal;
  semanas: import('@adelante/shared').SemanaOperativa[];
  semanaSel: number | null;
  filtroVenta: FiltroVenta;
}) {
  const { semana, totales, obras, base_semanal: baseSemanal } = reporte;
  // Vista de la tabla: producción de la SEMANA (default) o ACUMULADO a la fecha.
  const [vista, setVista] = useState<'semana' | 'acumulado'>('semana');
  const porProyecto = useMemo(() => {
    const map = new Map<string, typeof obras>();
    for (const o of obras) {
      const arr = map.get(o.proyecto_codigo) ?? [];
      arr.push(o);
      map.set(o.proyecto_codigo, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [obras]);

  // Valores por obra según la vista elegida.
  const valores = (o: ObraReporte) =>
    vista === 'semana'
      ? {
          pct: o.costo_semana,
          m2: o.m2_semana,
          directo: o.monto_semana,
          indirecto: o.indirecto_semana,
          venta: o.venta_semana,
          utilidad: o.utilidad_semana,
        }
      : {
          pct: o.costo_acumulado,
          m2: o.m2_acumulado,
          directo: o.monto_acumulado,
          indirecto: o.indirecto_acumulado,
          venta: o.venta_acumulada,
          utilidad: o.utilidad_acumulada,
        };
  // En vista Semana sin cierre previo, no hay "logrado de la semana" → "—".
  const mostrar = vista === 'acumulado' || baseSemanal;

  return (
    <div className="space-y-4">
      {/* Encabezado (se imprime) */}
      <Card className="space-y-1 p-4 print:border-0 print:p-0 print:shadow-none">
        <h2 className="text-xl font-semibold">
          Financiero — Semana {semana.numero_semana} · {semana.anio}
        </h2>
        <p className="text-xs text-muted-foreground">
          {semana.fecha_inicio.slice(0, 10)} → {semana.fecha_fin.slice(0, 10)} ·{' '}
          {semana.dias_efectivos} días efectivos · {semana.estado}
        </p>
        <p className="text-xs text-muted-foreground">
          Producción de Directo, Indirecto, Venta y Utilidad. Factor = % Costo Real aplicado a los
          presupuestos de la obra (Directo, Indirecto y Venta en ₡). Utilidad = Venta − Directo −
          Indirecto.
        </p>
      </Card>

      {!baseSemanal && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span>
            ⓘ La <strong>producción de la semana</strong> se calcula contra el cierre de la semana
            anterior. Todavía no existe, así que las cifras semanales aparecen como “—”. El
            acumulado sí es real.
          </span>
        </div>
      )}

      {/* Cards: valor = producido en la semana; sub-línea = acumulado a la fecha */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiFin
          label="Costo Directo"
          semana={totales.monto_semana}
          acum={totales.monto_acumulado}
          baseSemanal={baseSemanal}
        />
        <KpiFin
          label="Indirecto"
          semana={totales.indirecto_semana}
          acum={totales.indirecto_acumulado}
          baseSemanal={baseSemanal}
        />
        <KpiFin
          label="Venta"
          semana={totales.venta_semana}
          acum={totales.venta_acumulada}
          baseSemanal={baseSemanal}
          accent="lime"
        />
        <KpiFin
          label="Utilidad"
          semana={totales.utilidad_semana}
          acum={totales.utilidad_acumulada}
          baseSemanal={baseSemanal}
          accent={totales.utilidad_acumulada >= 0 ? 'lime' : 'red'}
        />
      </div>

      {/* Resumen del mes — producción financiera por semana. */}
      <div className="romper-pagina">
        <ResumenMesFinancieroVista
          semanas={semanas}
          semanaSel={semanaSel}
          filtroVenta={filtroVenta}
        />
      </div>

      {/* Toggle de vista de la tabla */}
      <div className="flex items-center gap-2 print:hidden">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Detalle por obra:
        </span>
        <div className="inline-flex rounded-md border border-border p-0.5">
          <button
            type="button"
            onClick={() => setVista('semana')}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              vista === 'semana'
                ? 'bg-adelante-primary text-adelante-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Semana
          </button>
          <button
            type="button"
            onClick={() => setVista('acumulado')}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              vista === 'acumulado'
                ? 'bg-adelante-primary text-adelante-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Acumulado
          </button>
        </div>
      </div>

      {/* Tabla por obra, agrupada por proyecto. */}
      <div className="romper-pagina space-y-4">
        {porProyecto.map(([proyecto, lista]) => {
          const sub = lista.reduce(
            (acc, o) => {
              const v = valores(o);
              return {
                directo: acc.directo + v.directo,
                indirecto: acc.indirecto + v.indirecto,
                venta: acc.venta + v.venta,
                utilidad: acc.utilidad + v.utilidad,
              };
            },
            { directo: 0, indirecto: 0, venta: 0, utilidad: 0 },
          );
          return (
            <Card key={proyecto} className="overflow-hidden print:shadow-none">
              <div className="border-b border-border bg-zinc-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Proyecto {proyecto} · {lista.length} obras ·{' '}
                {vista === 'semana' ? 'Vista Semana' : 'Vista Acumulado'}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs">Obra</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">% Costo Real</TableHead>
                    <TableHead className="text-right">m²</TableHead>
                    <TableHead className="text-right">Directo</TableHead>
                    <TableHead className="text-right">Indirecto</TableHead>
                    <TableHead className="text-right">Venta</TableHead>
                    <TableHead className="text-right">Utilidad</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lista.map((o) => {
                    const v = valores(o);
                    return (
                      <TableRow key={o.codigo} className={cn(obraParqueada(o) && 'opacity-50')}>
                        <CeldaObra codigo={o.codigo} />
                        <CeldaTipo tipo={o.tipo_casa} />
                        <TableCell className="text-right font-mono tabular-nums text-sm font-semibold text-adelante-primary-dark">
                          {mostrar ? `${v.pct}%` : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">
                          {mostrar ? fmtNum(v.m2) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">
                          {mostrar ? fmtMonto(v.directo) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">
                          {mostrar ? fmtMonto(v.indirecto) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">
                          {mostrar ? fmtMonto(v.venta) : '—'}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right font-mono tabular-nums text-sm font-semibold',
                            v.utilidad >= 0 ? 'text-adelante-primary-dark' : 'text-red-600',
                          )}
                        >
                          {mostrar ? fmtMonto(v.utilidad) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow className="border-t-2">
                    <TableCell className="whitespace-nowrap font-semibold">TOTAL</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                      {mostrar ? fmtMonto(sub.directo) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                      {mostrar ? fmtMonto(sub.indirecto) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
                      {mostrar ? fmtMonto(sub.venta) : '—'}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-mono tabular-nums text-xs font-semibold',
                        sub.utilidad >= 0 ? 'text-adelante-primary-dark' : 'text-red-600',
                      )}
                    >
                      {mostrar ? fmtMonto(sub.utilidad) : '—'}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/** Card financiera: valor grande = producido en la semana; sub-línea = acumulado. */
function KpiFin({
  label,
  semana,
  acum,
  baseSemanal,
  accent,
}: {
  label: string;
  semana: number;
  acum: number;
  baseSemanal: boolean;
  accent?: 'lime' | 'red';
}) {
  return (
    <Card className="p-3 print:border print:shadow-none">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'text-lg font-semibold tabular-nums',
          accent === 'lime' && 'text-adelante-primary-dark',
          accent === 'red' && 'text-red-600',
        )}
      >
        {baseSemanal ? formatearCRC(semana) : '—'}
      </p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">Acum: {formatearCRC(acum)}</p>
    </Card>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {titulo}
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
  sufijo,
  small,
  detalle,
}: {
  label: string;
  value: string | number;
  accent?: 'lime' | 'red';
  sufijo?: string;
  /** Para montos largos (₡): usa una fuente menor para que no desborde. */
  small?: boolean;
  /** Línea secundaria bajo el valor (texto chico). */
  detalle?: string;
}) {
  return (
    <Card className="p-3 print:border print:shadow-none">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'font-semibold tabular-nums',
          small ? 'text-lg' : 'text-2xl',
          accent === 'lime' && 'text-adelante-primary-dark',
          accent === 'red' && 'text-red-600',
        )}
      >
        {value}
        {sufijo && <span className="ml-1 text-xs font-normal text-muted-foreground">{sufijo}</span>}
      </p>
      {detalle && <p className="mt-0.5 text-[11px] text-muted-foreground">{detalle}</p>}
    </Card>
  );
}

/** Cumplimiento (%) redondeado a entero; '—' si no hay esperado. */
function fmtCumpl(n: number | null): string {
  return n == null ? '—' : `${Math.round(n)}%`;
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** ₡ compacto para celdas de tabla (₡5.8M, ₡320k). */
function fmtMonto(n: number): string {
  if (!n) return '—';
  if (Math.abs(n) >= 1_000_000)
    return `₡${(n / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(n) >= 1_000)
    return `₡${(n / 1_000).toLocaleString('en-US', { maximumFractionDigits: 0 })}k`;
  return `₡${n.toLocaleString('en-US')}`;
}
